import { ComponentFactory, Image, Label, LabelSource, Panel, TreeNode } from "@mcleod/components";
import { Alignment, ModelRow, ObjectUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { MenuEditorTreeNode } from "./MenuEditorTreeNode";
import { RowWebMenuGroup } from "../models/ModelWebMenuGroup";
import { RowWebMenuGroupFields } from "../models/autogen/AutogenModelWebMenuGroup";
import { RowWebMenuItemFields } from "../models/autogen/AutogenModelWebMenuItem";
import { RowWebMenuItem } from "../models/ModelWebMenuItem";
import { MenuBranchSelectorTreeNode } from "./MenuBranchSelector";

export class MenuEditorUtil {
    static addSharedMenuIndicator(label: Label, node: MenuEditorTreeNode | MenuBranchSelectorTreeNode) {
        if (node.menuItem.groupReferenceData) {
            const image = new Image({
                name: ImageName.link,
                color: "subtle.darker",
                rowBreak: false,
                width: 18,
                height: 18,
                marginLeft: 4
            });

            image.tooltipDynamicContent = () => {
                const nodesSharingGroupId = (otherNode: MenuEditorTreeNode) => {
                    return otherNode !== node && otherNode.menuItem.groupData.get("id") === node.menuItem.groupData.get("id");
                }
                const otherNodes = node.tree.getRootNode().getRecursiveChildNodes(nodesSharingGroupId);
                const msg = {text: `${node.menuItem.caption} is a Shared Menu used in the following locations`, fontBold: true};
                return this.createReferencePanel(msg, [node, ...otherNodes]);
            };
            image.tooltipDynamicOptions = { position: Alignment.RIGHT };

            label._element.appendChild(image._element);
        }
    }

    static createReferencePanel(msg: LabelSource, nodes: TreeNode[]): Panel {
        const panel = Panel.createNoMarginNoPaddingPanel({
            fillHeight: true, fillRow: true
        });

        const paths = nodes.map(node => node.getPathAsString());
        const msgLabel = ComponentFactory.createCommon(msg, {marginBottom: 12});
        panel.add(msgLabel);
        panel.add(...ComponentFactory.createBulletedList(paths));
        return panel;
    }

    static addReferencePanel(msg: LabelSource, nodes: MenuEditorTreeNode[], panel: Panel) {
        if (nodes == null || nodes.length < 2 || msg == null)
            return;
        panel.add(this.createReferencePanel(msg, nodes));
    }

    static resolveGroupRow(value: RowWebMenuGroup | Partial<RowWebMenuGroupFields>, appending: boolean): RowWebMenuGroup {
        return this.resolveRow(value, RowWebMenuGroup, appending);
    }

    static resolveItemRow(value: RowWebMenuItem | Partial<RowWebMenuItemFields>, appending: boolean): RowWebMenuItem {
        return this.resolveRow(value, RowWebMenuItem, appending);
    }

    static resolveRow<T extends ModelRow>(
        value: T | Partial<T['data']>,
        constructor: new (data: any) => T,
        appending: boolean
    ): T {
        if (ObjectUtil.isEmptyObject(value)) {
            return null;
        }

        let result: T;
        if (value instanceof ModelRow) {
            result = new constructor(value.data);
        } else {
            result = new constructor(value);
        }

        result._appending = appending;
        result.resetOriginalData();
        return result;
    }
}
